import { yupResolver } from '@hookform/resolvers/yup'
import type { BaseSyntheticEvent } from 'react'
import { useCallback, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

import { Button } from '@src/components/Button/Button'
import { Dropdown } from '@src/components/Dropdown/Dropdown'
import { ModalContent } from '@src/components/Modal/ModalContent'
import { Toast } from '@src/components/Toaster/Toast'
import type { LanguageSchema } from '@src/constants/AccountSchema'
import { languageSchema } from '@src/constants/AccountSchema'
import { useSession } from '@src/hooks/useSession'
import { useLocale } from '@src/i18n/LocaleProvider'
import { getLocaleCodes } from '@src/i18n/locale.util'
import type { CountryCodeTBB, LangTBB, LocaleCode, LocaleCodeTBB } from '@src/i18n/locales'
import { ServerActions } from '@src/services/server-actions.service'
import { TBB_LANGUAGE_OPTIONS } from '@src/utils/constants.util'

interface Props {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  loading: boolean
  setLoading: (loading: boolean) => void
}

export function ChangeLanguageForm({ isOpen, setIsOpen, loading, setLoading }: Props) {
  const { t } = useLocale('accountPage')
  const { t: tc } = useLocale('countries')
  const { t: tl } = useLocale('languages')
  const { user, refreshToken } = useSession()

  const languageForm = useForm<LanguageSchema>({
    mode: 'onTouched',
    disabled: loading,
    defaultValues: {
      country: user?.userCountry,
      lang: user?.userLang,
    },
    resolver: yupResolver(languageSchema),
  })

  useEffect(() => {
    if (isOpen) return
    languageForm.reset({ country: user?.userCountry, lang: user?.userLang })
  }, [languageForm, isOpen, user])

  const [country] = languageForm.watch(['country', 'lang'])

  const countryDropdownItems = TBB_LANGUAGE_OPTIONS.map((country) => ({
    id: country.countryCode,
    displayName: tc(country.countryCode),
    name: tc(country.countryCode),
  }))

  const languageDropdownItems = TBB_LANGUAGE_OPTIONS.find(
    (c) => c.countryCode === country
  )?.languages.map((lang) => {
    const code = `${lang}-${country}` as LocaleCodeTBB
    const isSupported = getLocaleCodes().includes(code as LocaleCode)
    const supported = isSupported ? '' : ` ${t('unsupported')}`
    return {
      id: lang,
      displayName: tl(lang) + supported,
      name: tl(lang) + supported,
    }
  })

  const onLanguageFormSubmit = useCallback(
    async (data: LanguageSchema, event?: BaseSyntheticEvent) => {
      try {
        event?.preventDefault()
        if (loading) return
        setLoading(true)
        const preferredLanguage = `${data.lang}-${data.country}` as LocaleCodeTBB
        await ServerActions.serial.updateAccount({ preferredLanguage })
        await refreshToken()
        toast(() => <Toast message={t('languageUpdated')} variant="success" />)
      } catch (error) {
        toast(() => <Toast message={t('error')} variant="error" />)
      } finally {
        setLoading(false)
        setIsOpen(false)
      }
    },
    [loading, refreshToken, setIsOpen, setLoading, t]
  )

  return (
    <ModalContent
      size="medium"
      position="center"
      isOpen={isOpen}
      onClose={() => {
        if (loading) return
        setIsOpen(false)
      }}
      title={t('editLanguage')}
    >
      <form onSubmit={languageForm.handleSubmit(onLanguageFormSubmit)}>
        <div className="flex flex-col gap-4">
          <Dropdown
            listboxOptionsClassNames="z-[1000]"
            placeholder={t('select-your-country')}
            items={countryDropdownItems}
            selectedId={languageForm.getValues().country}
            onItemIdSelected={(id) => {
              const option = TBB_LANGUAGE_OPTIONS.find((c) => c.countryCode === id)
              languageForm.setValue('country', option?.countryCode as CountryCodeTBB)
              languageForm.setValue('lang', option?.languages[0] as LangTBB)
            }}
          />
          <Dropdown
            listboxOptionsClassNames="z-[1000]"
            placeholder={t('select-your-country')}
            items={languageDropdownItems ?? []}
            selectedId={languageForm.getValues().lang}
            onItemIdSelected={(id) => {
              languageForm.setValue('lang', id as LangTBB)
            }}
          />
          <div className="mt-4 flex flex-col gap-2 lg:flex-row">
            <Button onClick={() => {}} size="large" isLoading={loading}>
              {t('update')}
            </Button>
            <Button
              size="large"
              variant="secondary"
              disabled={loading}
              onClick={() => {
                if (loading) return
                setIsOpen(false)
              }}
            >
              {t('cancel')}
            </Button>
          </div>
        </div>
      </form>
    </ModalContent>
  )
}
